// AI 教练浮窗的会话状态（登录后全站可用，路由切换不丢）
//
// 三个关键设计：
// 1) 流式请求由 store 持有（AbortController 放模块作用域，不进 state）：
//    浮窗收起/展开、路由切换都不会打断正在生成的一轮对话，组件只负责渲染；
// 2) 气泡划分以 assistant_start 为准：工具往返会让一次提问产生多条 assistant 消息。
//    assistant_start 只带 round（本轮消息要等结束才落库，没有 message_id），
//    因此实时气泡用 round + 自增序号生成局部 key，历史加载时才用数据库消息 id；
// 3) 事件应用函数（applyXxx）与 send() 分离：send 只负责编排请求与错误兜底。

import { defineStore } from "pinia";
import { AiInterruptedError, AiRequestError, PostAiChat } from "@/apis/ai.ts";
import type { AiAction, AiContextItem, AiStreamUsage } from "@/apis/ai.ts";
import { GetAiConversation, GetAiConversationList, GetAiScene } from "@/apis/ai-conversation.ts";
import type { AiConversationDetail, AiConversationItem, AiMessageView, AiSceneMeta, AiSceneMetaResult } from "@/apis/ai-conversation.ts";
import { GetText } from "@/text/library.ts";
import { GetAiCoachPageTitle } from "@/stores/aiCoachEntry.ts";
import type { AiCoachPageEntry } from "@/stores/aiCoachEntry.ts";

/** 场景引用的对象（judge / problem / contest / ''） */
export interface AiCoachRef {
  type: string;
  id: number;
}

/** 实时渲染用的一次工具调用 */
export interface AiCoachToolCall {
  call_id: string;
  name: string;
  args?: Record<string, unknown>;
}

/** 实时渲染用的一次工具结果 */
export interface AiCoachToolResult {
  call_id: string;
  /** 工具 id（用于展示中文名；历史消息里由服务端给出） */
  name?: string;
  ok: boolean;
  summary?: string;
}

/**
 * 桌面端浮窗的位置与尺寸（视口坐标系，单位 px）。
 * 拖动/缩放只改这份状态，浮窗壳用内联样式消费它，因此「记忆」与「渲染」不会两处各写一套。
 */
export interface AiCoachPanelRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * 右下角入口气泡的位置（左上角，视口坐标系，单位 px）。
 * null 表示用户从未拖动过：气泡仍贴默认右下角（由 CSS 的 right/bottom 给出），
 * 这样窗口大小变化时未拖动的气泡始终跟随视口右下角；一旦拖动过就记忆绝对位置。
 */
export interface AiCoachBubblePos {
  x: number;
  y: number;
}

/** 浮窗内的一条消息（user / assistant / tool） */
export interface AiCoachMessage {
  /** 局部键：实时消息用 round 生成，历史消息用数据库 id */
  key: string;
  role: "user" | "assistant" | "tool";
  content: string;
  thinking: string;
  toolCalls: AiCoachToolCall[];
  toolResults: AiCoachToolResult[];
  actions: AiAction[];
  generating: boolean;
  /** 生成被中断（或用户主动停止）：内容可能不完整 */
  interrupted: boolean;
  /** 中断说明（interrupted 为 true 时展示） */
  interruptedMessage: string;
  /** 数据库消息 id（实时流在本轮结束前为 0） */
  messageId: number;
}

interface AiCoachState {
  /** 浮窗是否展开（false = 完全不显示，只剩右下角入口气泡） */
  visible: boolean;
  /** 折叠成圆形气泡（仅桌面端） */
  collapsed: boolean;
  scene: string;
  ref: AiCoachRef;
  conversationId: number;
  messages: AiCoachMessage[];
  draft: string;
  generating: boolean;
  errorCode: number;
  errorMessage: string;
  lastUsage: AiStreamUsage | null;
  /**
   * 打开教练时所在页面（路由 name，如 problem-detail）。
   * 新建会话时随请求上报给后端落库（统计「用户从哪里问起」）；浮窗提示与漂移检测也用它。
   * 载入历史会话时改为会话落库的 entry_page（统计口径与展示一致）。
   */
  entryPage: string;
  /** entryPage 的展示名（中文，由页面入口注册表给出） */
  entryPageTitle: string;
  /**
   * 打开教练时所在页面的真实访问路径（route.path + 查询参数名，如 /problem/P1001；
   * 查询参数的取值不入库，见 BuildAiCoachEntryPath）。
   * 与 entryPage 一起在新建会话时上报落库，供统计回答「具体在哪道题 / 哪次提交上问起」；
   * 空串表示旧数据或当前入口没给（不影响其它逻辑）。
   */
  entryPath: string;
  /**
   * 页面漂移：用户已经离开打开教练时的页面（当前路由注册了教练入口，且其场景/绑定对象
   * 与 store 当前的不一致）。为 true 时上下文条把「在哪个页面打开」的说明更新为
   * 「基于哪个页面、当前在哪个页面」，并给出轻量的「改用当前页面」入口——不弹整条警告。
   * pageDriftEntry 携带当前页面的入口（含 openOpts），由 AiCoachPanel 监听路由变化后调用
   * detectPageDrift 写入——store 不依赖 router，判定输入由外部给。
   */
  pageDrifted: boolean;
  pageDriftEntry: AiCoachPageEntry | null;
  /** 当前场景是否可用（/ai/scene 未返回该场景，或服务端回了 100095 时为 false） */
  sceneEnabled: boolean;
  /** 场景展示名（/ai/scene 的 title） */
  sceneTitle: string;
  /**
   * 场景级提示：由打开浮窗的页面按当前对象的元信息给出，
   * 例如提交详情页在 ai_parse.enabled=false 时带进来的禁用原因（比赛进行中/评测未结束）。
   * 非空时浮窗展示该提示并禁用输入区——不让用户先发一条注定失败的消息。
   */
  sceneNotice: string;
  sceneList: AiSceneMeta[];
  /** BYOK 是否就绪（false 时输入区提示去设置；发送仍会走到 10011 的既有路径） */
  byokReady: boolean;
  /**
   * 当前上下文清单（本次对话「带了什么 / 能查什么 / 看不到什么」）。
   *
   * 两个来源刻意共用一份状态，因为它们同源：
   * - 打开浮窗时 /ai/scene 的 context 预览（发问之前就能看到）；
   * - 对话首帧的 context 事件（真正发给模型的那一份，覆盖预览）。
   * 前端只做分组渲染，文案一律来自服务端。
   */
  contextItems: AiContextItem[];
  /** 上下文预览不可用时的原因（项目错误码，0 = 无）：如比赛进行中、无权查看 */
  contextCode: number;
  provider: string;
  model: string;
  sceneMetaLoaded: boolean;
  /**
   * 桌面端浮窗的位置与尺寸（Task C3）：可拖动/缩放并持久化，刷新后恢复。
   * 初值取默认几何（右下角、400 × min(70vh, 640)），首次 hydrate 时若视口更小会被钳制回来。
   * 移动端仍走整屏 t-drawer，完全忽略这份状态。
   */
  panelRect: AiCoachPanelRect;
  /**
   * 右下角入口气泡的位置（Task C5）：null = 从未拖动过，仍贴默认右下角；
   * 拖动后记忆绝对位置并持久化。气泡常驻右下角会挡住页面内容（如提交按钮、分页器），
   * 支持拖动让用户把它挪到不碍事的地方。
   */
  bubblePos: AiCoachBubblePos | null;
  /** 是否有待填充的预填草稿：只在「拿到场景模板后」消费一次（见 applyPromptPrefill） */
  promptDraftPending: boolean;
  /**
   * 当前上下文的优先关键字（逗号分隔）：用于把命中关键字的模板排到最前。
   * 由打开浮窗的页面给出（如提交详情页按判题状态给「问题出在哪里」），浮窗本身不理解语义。
   */
  promptKeywords: string;
  /** 浮窗内「会话切换」用的最近会话列表 */
  conversations: AiConversationItem[];
  conversationsLoaded: boolean;
  /** 输入框聚焦请求：自增即触发组件聚焦一次（open 的 autoFocus） */
  focusSignal: number;
  /**
   * 「解析已保存」信号：保存成功后自增，供提交详情页同步已保存解析卡片。
   * 之所以用信号而不是整页刷新：详情页重拉会重建 Monaco 内容、丢失滚动位置。
   * parseSavedJudgeId 记录刚保存的是哪次提交，避免在别的提交页面误触发。
   */
  parseSavedTick: number;
  parseSavedJudgeId: number;
  /** 气泡序号：round 每次请求都从 1 重新开始，需配自增序号保证 key 唯一 */
  bubbleSeq: number;
  /** 当前正在写入的气泡 key */
  activeBubbleKey: string;
}

// 当前请求的中止控制器。刻意不放 state：它不是渲染数据，且会破坏 pinia 的可序列化语义
let abortController: AbortController | null = null;

// 场景元信息请求的序号：快速连续 open（A 页打开后立刻在 B 页打开）时两次 refreshSceneMeta
// 并发返回、顺序不定，旧请求晚回会把新页面的上下文预览覆盖掉。序号只认最后一次请求。
let sceneMetaSeq = 0;

/** 错误码优先查项目文案表，取不到则回退服务端原文（与 AIEnhancement.vue 同一套范式） */
export function ResolveAiErrorText(message: string, code?: number): string {
  if (code) {
    const [found, text] = GetText(code);
    if (found) {
      return text;
    }
  }
  return message;
}

// ── 桌面端浮窗几何（Task C3）──
//
// 默认几何刻意与改造前的 CSS 完全一致（右下角 24px、宽 400px、高 min(70vh, 640px)），
// 这样「双击头部还原」「首次打开」看到的就是原来的浮窗，不引入视觉回归。

/** 浮窗默认宽度 */
const PANEL_DEFAULT_WIDTH = 400;
/** 浮窗默认高度上限（与 CSS 的 min(70vh, 640px) 对齐） */
const PANEL_DEFAULT_MAX_HEIGHT = 640;
/** 浮窗与视口右下角的默认间距 */
const PANEL_MARGIN = 24;
/** 浮窗最小尺寸（缩放手柄的下限，也是小视口下的兜底尺寸） */
const PANEL_MIN_WIDTH = 320;
const PANEL_MIN_HEIGHT = 360;
/** 浮窗必须保留在视口内的可见尺寸：拖到任何位置都至少露出这么多，不允许被完全拖出屏幕 */
const PANEL_KEEP_VISIBLE = 48;
/** 入口气泡边长（与 AiCoachPanel.vue 里 .ai-coach-bubble 的 48px 对齐） */
const BUBBLE_SIZE = 48;

/** 视口尺寸；服务端渲染/单测等无 window 场景下给一个兜底值 */
function panelViewportSize(): { width: number; height: number } {
  if (typeof window === "undefined") {
    return { width: 1440, height: 900 };
  }
  return { width: window.innerWidth, height: window.innerHeight };
}

/** 浮窗默认位置与尺寸（右下角） */
function defaultPanelRect(): AiCoachPanelRect {
  const viewport = panelViewportSize();
  return {
    left: viewport.width - PANEL_MARGIN - PANEL_DEFAULT_WIDTH,
    top: viewport.height - PANEL_MARGIN - Math.min(viewport.height * 0.7, PANEL_DEFAULT_MAX_HEIGHT),
    width: PANEL_DEFAULT_WIDTH,
    height: Math.min(viewport.height * 0.7, PANEL_DEFAULT_MAX_HEIGHT),
  };
}

/** 四舍五入到整数像素：写入 store（进而写进 localStorage）的几何值保持整数，避免出现 37.60000000000002 这类脏值 */
function roundPanelRect(rect: AiCoachPanelRect): AiCoachPanelRect {
  return {
    left: Math.round(rect.left),
    top: Math.round(rect.top),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
}

/**
 * 把浮窗几何钳制到「可见且不超出视口」：拖动、缩放、窗口 resize 回收都复用这一份规则。
 *
 * 规则（顺序不能颠倒）：
 * 1) 尺寸：不小于最小值，不大于视口减去两侧间距（略小于视口，避免铺满后失去浮窗语义）；
 *    视口本身比最小值还小时（极端窄屏 / 压扁的窗口）以视口为准，保证浮窗仍在屏内；
 * 2) 位置：至少保留 PANEL_KEEP_VISIBLE 的可见宽度/高度，
 *    因此左边界允许到 -(width - KEEP)，右边界最多到 viewport.width - KEEP，上边界同理。
 */
export function ClampPanelRect(rect: AiCoachPanelRect, viewport = panelViewportSize()): AiCoachPanelRect {
  // 视口上限；视口本身比最小尺寸还小时允许小于最小值（宁可压扁也不能让底部按钮伸出屏幕外）
  const maxWidth = Math.max(0, viewport.width - PANEL_MARGIN * 2);
  const maxHeight = Math.max(0, viewport.height - PANEL_MARGIN * 2);
  const width = Math.min(Math.max(rect.width, Math.min(PANEL_MIN_WIDTH, maxWidth)), maxWidth);
  const height = Math.min(Math.max(rect.height, Math.min(PANEL_MIN_HEIGHT, maxHeight)), maxHeight);
  const minLeft = -(width - PANEL_KEEP_VISIBLE);
  const maxLeft = viewport.width - PANEL_KEEP_VISIBLE;
  const minTop = -(height - PANEL_KEEP_VISIBLE);
  const maxTop = viewport.height - PANEL_KEEP_VISIBLE;
  return {
    left: Math.min(Math.max(rect.left, minLeft), maxLeft),
    top: Math.min(Math.max(rect.top, minTop), maxTop),
    width,
    height,
  };
}

/**
 * 直接取用默认几何（打开浮窗 / 双击头部还原 / 视口刚加载时用）。
 * 先钳尺寸、再按最终尺寸贴右下角：若按「钳制前的高度」算位置，视口较矮时高度会被钳大、
 * 底部就会伸出视口（输入框与发送按钮被切掉）。
 */
export function DefaultPanelRect(): AiCoachPanelRect {
  const viewport = panelViewportSize();
  const rect = ClampPanelRect(defaultPanelRect());
  return ClampPanelRect({
    ...rect,
    left: viewport.width - PANEL_MARGIN - rect.width,
    top: viewport.height - PANEL_MARGIN - rect.height,
  });
}

/** 把气泡位置钳制在视口内：入口必须完整可见，不允许拖到屏幕外后找不到 */
export function ClampBubblePos(pos: AiCoachBubblePos, viewport = panelViewportSize()): AiCoachBubblePos {
  const maxX = Math.max(0, viewport.width - BUBBLE_SIZE);
  const maxY = Math.max(0, viewport.height - BUBBLE_SIZE);
  return {
    x: Math.min(Math.max(pos.x, 0), maxX),
    y: Math.min(Math.max(pos.y, 0), maxY),
  };
}

/**
 * 拆上下文优先关键字：支持中英文逗号分隔、忽略空白与空项。
 * 导出给对话区复用（AiChatView 用它决定模板 chips 的排序）。
 */
export function SplitPromptKeywords(keywords: string): string[] {
  return (keywords ?? "")
    .split(/[,，]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

/** 创建一条消息（补齐所有数组字段，避免渲染处到处判空） */
function CreateMessage(partial: Partial<AiCoachMessage> & Pick<AiCoachMessage, "key" | "role">): AiCoachMessage {
  return {
    content: "",
    thinking: "",
    toolCalls: [],
    toolResults: [],
    actions: [],
    generating: false,
    interrupted: false,
    interruptedMessage: "",
    messageId: 0,
    ...partial,
  };
}

/**
 * 动作签名：后端会在 action 事件与 done 事件里下发同一批动作，
 * 用「类型 + 文案 + 载荷」去重，避免同一张卡片出现两次。
 */
function ActionSignature(action: AiAction): string {
  return `${action.type}|${action.label}|${JSON.stringify(action.payload ?? {})}`;
}

/** 历史工具结果只用于折叠展示：取首行并截断，避免把整段工具正文塞进气泡 */
function ToolSummaryOf(content: string): string {
  const firstLine = (content ?? "").split("\n")[0].trim();
  return firstLine.length > 60 ? `${firstLine.slice(0, 60)}…` : firstLine;
}

/**
 * 历史消息 → 气泡。
 *
 * 关键点：服务端把 role=tool 作为独立消息落库（否则下一轮重建上下文会丢工具结果），
 * 而实时流里工具结果挂在 assistant 气泡上。这里把 tool 消息并回它前面那条 assistant 气泡，
 * 保证「历史回溯」与「实时对话」是同一种渲染形态；找不到归属时才单独成条。
 *
 * 导出给主界面复用（views/ai/AiConversationView.vue）：
 * 主界面与浮窗共用同一份 store，因此「历史消息 → 可渲染结构」也只能有一套规则。
 */
export function MapHistoryMessages(views: AiMessageView[]): AiCoachMessage[] {
  const messages: AiCoachMessage[] = [];
  views.forEach((view, index) => {
    const role = view.role === "user" || view.role === "tool" ? view.role : "assistant";
    const toolResults = (view.tool_results ?? []).map((item) => ({
      call_id: item.call_id,
      name: item.name,
      // 是否成功不单独落库（失败时正文是给模型看的降级文本），历史里统一按「已读取」展示
      ok: true,
      summary: ToolSummaryOf(item.content),
    }));

    if (role === "tool") {
      const owner = toolResults.length > 0 ? FindToolResultOwner(messages, toolResults[0].call_id) : undefined;
      if (owner) {
        owner.toolResults.push(...toolResults);
        return;
      }
      messages.push(
        CreateMessage({
          key: `h-tool-${view.id}-${index}`,
          role: "tool",
          content: view.content,
          toolResults,
          messageId: view.id,
        }),
      );
      return;
    }

    messages.push(
      CreateMessage({
        key: `h-${view.id}-${index}`,
        role,
        content: view.content,
        thinking: view.thinking ?? "",
        toolCalls: (view.tool_calls ?? []).map((item) => ({
          call_id: item.call_id,
          name: item.name,
          args: ParseToolArguments(item.arguments),
        })),
        actions: (view.actions ?? []).map((action) =>
      // 问答卡片在历史里一律只读：那一轮早已结束，用户要么已经回答、要么已经离开，
      // 让一个陈旧的选项继续可点会让人以为「现在选还来得及」（与 judge_parse_save
      // 在历史里因缺少回执而禁用是同一种处理）
      action.type === "ask_user"
        ? { ...action, disabled: true, reason: "该问题已回答或已失效" }
        : action,
    ),
        messageId: view.id,
      }),
    );
  });
  return messages;
}

/** 解析模型给出的参数文本：不保证合法，解析失败就当没有参数（只影响展示） */
function ParseToolArguments(text: string): Record<string, unknown> | undefined {
  if (!text) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(text);
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
}

/** 找到某次工具调用所属的 assistant 气泡（历史消息里 tool 消息紧跟在请求它的 assistant 之后） */
function FindToolResultOwner(messages: AiCoachMessage[], callId: string): AiCoachMessage | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== "assistant") {
      continue;
    }
    if (message.toolCalls.some((call) => call.call_id === callId)) {
      return message;
    }
    // 继续往前找：工具往返会产生多条 assistant 消息
  }
  return undefined;
}

export const useAiCoachStore = defineStore("aiCoach", {
  state: (): AiCoachState => ({
    visible: false,
    collapsed: false,
    scene: "free_chat",
    ref: { type: "", id: 0 },
    conversationId: 0,
    messages: [],
    draft: "",
    generating: false,
    errorCode: 0,
    errorMessage: "",
    lastUsage: null,
    entryPage: "",
    entryPageTitle: "",
    entryPath: "",
    pageDrifted: false,
    pageDriftEntry: null,
    sceneEnabled: true,
    sceneTitle: "",
    sceneNotice: "",
    sceneList: [],
    byokReady: true,
    contextItems: [],
    contextCode: 0,
    provider: "",
    model: "",
    sceneMetaLoaded: false,
    panelRect: DefaultPanelRect(),
    bubblePos: null,
    promptDraftPending: false,
    promptKeywords: "",
    conversations: [],
    conversationsLoaded: false,
    focusSignal: 0,
    parseSavedTick: 0,
    parseSavedJudgeId: 0,
    bubbleSeq: 0,
    activeBubbleKey: "",
  }),

  getters: {
    /** 头部展示用：优先服务端场景名，其次场景 key */
    sceneDisplayTitle(state): string {
      if (state.sceneTitle) {
        return state.sceneTitle;
      }
      return state.sceneList.find((item) => item.key === state.scene)?.title ?? state.scene;
    },
    /** 当前正在写入的气泡（没有则返回 undefined） */
    activeBubble(state): AiCoachMessage | undefined {
      return state.messages.find((message) => message.key === state.activeBubbleKey);
    },
    /**
     * 上下文清单按 kind 分组（前端只分组，不发散文案）。
     *
     * 顺序与分组固定为「已注入 → 可按需查询 → 不可见」：
     * 用户最先要知道的是「它已经看到了什么」，其次是「看不到什么」——
     * 后者往往是回答不可信的边界，所以也必须在折叠态就能看到（见 AiChatView 的上下文条）。
     */
    contextGroups(state): { injected: AiContextItem[]; onDemand: AiContextItem[]; blocked: AiContextItem[] } {
      const groups = { injected: [] as AiContextItem[], onDemand: [] as AiContextItem[], blocked: [] as AiContextItem[] };
      state.contextItems.forEach((item) => {
        if (item.kind === "injected") {
          groups.injected.push(item);
        } else if (item.kind === "on_demand") {
          groups.onDemand.push(item);
        } else {
          // 未知 kind 归入「不可见」：宁可让用户保守，也不能让他以为拿到了什么
          groups.blocked.push(item);
        }
      });
      return groups;
    },
  },

  actions: {
    /**
     * 打开浮窗。场景或引用与当前不同时直接开新会话：
     * 旧对话的上下文属于另一个对象，继续追问会串题。
     *
     * notice 是场景级提示（如详情页带来的禁用原因）：只在调用方显式传入时更新，
     * 传空串即清除（详情页在 ai_parse.enabled=true 时会传空串）；
     * 从右下角气泡入口打开时不传，保留上次的提示。
     *
     * draft 是「预填一行草稿」（只填充输入框、不自动发送），语义是「这次打开要问什么」：
     * 因此它总是覆盖当前草稿（用户刚点了「AI 解析」，意图明确）；
     * promptKeywords 是上下文优先关键字，用于把命中的模板排到最前，传空串即清除。
     *
     * entryPage / entryPageTitle / entryPath 记录「从哪里打开」：传了才更新（气泡入口保留上次的，
     * 页面入口会传当前路由），随新建会话上报给后端做统计。
     */
    async open(
      opts: {
        scene?: string;
        ref?: AiCoachRef;
        autoFocus?: boolean;
        notice?: string;
        draft?: string;
        promptKeywords?: string;
        entryPage?: string;
        entryPageTitle?: string;
        entryPath?: string;
      } = {},
    ) {
      const scene = opts.scene ?? this.scene;
      const ref = opts.ref ?? this.ref;
      const changed = scene !== this.scene || ref.type !== this.ref.type || ref.id !== this.ref.id;
      this.visible = true;
      this.collapsed = false;
      if (changed) {
        this.reset();
        this.scene = scene;
        this.ref = { type: ref.type, id: ref.id };
        // 场景换了，旧的场景元信息（可用性、标题）作废
        this.sceneMetaLoaded = false;
      }
      if (opts.notice !== undefined) {
        this.sceneNotice = opts.notice;
      }
      // 关键字不传即清除：上次打开留下的排序偏好不该影响这一次
      this.promptKeywords = (opts.promptKeywords ?? "").trim();
      if (opts.draft !== undefined) {
        // 显式给了草稿：以调用方的文案为准（含传空串＝清空）
        this.draft = opts.draft;
        this.promptDraftPending = false;
      } else {
        // 只给了关键字：草稿要等场景模板到位后才决定（模板是异步加载的，此刻还不知道该填哪条）
        this.promptDraftPending = this.promptKeywords.length > 0;
      }
      if (opts.entryPage !== undefined) {
        this.entryPage = opts.entryPage;
        // 没给展示名时按注册表翻译，而不是把路由 name 当标题（未登记的 key 原样回退）
        this.entryPageTitle = opts.entryPageTitle ?? GetAiCoachPageTitle(opts.entryPage);
      }
      // 路径与页面名同来同去：只传页面名（旧调用点）时清掉上一次的路径，避免张冠李戴
      if (opts.entryPage !== undefined || opts.entryPath !== undefined) {
        this.entryPath = opts.entryPath ?? "";
      }
      if (opts.autoFocus) {
        this.focusSignal++;
      }
      await this.refreshSceneMeta();
    },

    /** 关闭浮窗：保留会话与消息，重新打开时接着聊 */
    close() {
      this.visible = false;
      this.collapsed = false;
    },

    /** 折叠为右下角气泡（桌面端） */
    setCollapsed(collapsed: boolean) {
      this.collapsed = collapsed;
    },

    // ── 桌面端浮窗几何（Task C3）──

    /** 拖动/缩放中：写入新几何并按视口钳制（调用方给的是完整矩形，避免两处各算一半） */
    setPanelRect(rect: AiCoachPanelRect) {
      this.panelRect = roundPanelRect(ClampPanelRect(rect));
    },

    /** 位置或尺寸变化结束（pointerup / 视口回收）：再钳制一次，保证落盘的几何一定可见 */
    normalizePanelRect() {
      this.panelRect = roundPanelRect(ClampPanelRect(this.panelRect));
    },

    /**
     * 窗口 resize 回收：只做「记忆」不回收，会出现「把窗口缩小后刷新，浮窗不见了」。
     * 用当前视口重新钳制；几何没变就不写 state，避免拖动过程中被无谓地打断。
     */
    clampPanelRectToViewport() {
      const next = roundPanelRect(ClampPanelRect(this.panelRect));
      const current = this.panelRect;
      if (next.left === current.left && next.top === current.top && next.width === current.width && next.height === current.height) {
        return;
      }
      this.panelRect = next;
    },

    /** 双击头部：还原默认位置与尺寸（右下角 400 × min(70vh, 640)） */
    resetPanelRect() {
      this.panelRect = DefaultPanelRect();
    },

    // ── 入口气泡位置（Task C5）──

    /** 气泡拖动中：写入新位置并按视口钳制（与 setPanelRect 同一套范式，调用方给完整坐标） */
    setBubblePos(pos: AiCoachBubblePos) {
      this.bubblePos = ClampBubblePos(pos);
    },

    /** 气泡拖动结束（pointerup / 视口回收）：取整并再钳制一次，保证落盘的坐标干净且可见 */
    normalizeBubblePos() {
      if (!this.bubblePos) {
        return;
      }
      this.bubblePos = ClampBubblePos({ x: Math.round(this.bubblePos.x), y: Math.round(this.bubblePos.y) });
    },

    /**
     * 窗口 resize 回收：把用户拖动过的气泡拉回可见区（缩小窗口后刷新，入口不会丢）。
     * null（从未拖动）无需处理——贴角位置由 CSS 给出，天然跟随视口右下角。
     */
    clampBubblePosToViewport() {
      if (!this.bubblePos) {
        return;
      }
      const next = ClampBubblePos(this.bubblePos);
      if (next.x !== this.bubblePos.x || next.y !== this.bubblePos.y) {
        this.bubblePos = next;
      }
    },

    /** 场景元信息（可用场景 + BYOK 是否就绪）：失败不阻塞使用，真正不可用时 /ai/chat 会返回错误码 */
    async refreshSceneMeta(force = false) {
      if (this.sceneMetaLoaded && !force) {
        return;
      }
      // 竞态保护：只认最后一次请求（见 sceneMetaSeq 的注释）。旧请求即使晚回也整体丢弃，
      // 不会把上一个页面/对象的上下文预览覆盖到当前界面上
      const seq = ++sceneMetaSeq;
      try {
        // 带上当前场景与引用：服务端据此顺带算出上下文预览（走与对话同一个场景 Prepare，因此同源）
        const res = await GetAiScene({ scene: this.scene, ref: this.ref });
        if (seq !== sceneMetaSeq) {
          return;
        }
        if (res.code !== 0) {
          return;
        }
        const data = res.data as AiSceneMetaResult;
        this.byokReady = data.byok_ready !== false;
        this.provider = data.provider ?? "";
        this.model = data.model ?? "";
        this.sceneList = data.scenes ?? [];
        // 旧后端不返回 context：此时保留现有清单，不因为一次拉取失败/字段缺失把已有信息抹掉
        if (data.context) {
          this.applyContextItems(data.context.items ?? [], data.context.available ? 0 : data.context.code ?? 0);
        }
        this.sceneMetaLoaded = true;
        this.applySceneMeta();
      } catch (e) {
        console.error("load ai scene meta failed:", e);
      } finally {
        // 模板是异步来的，预填只能等拿到之后（或确认拿不到）再决定：
        // 放在 finally 里，请求失败也不会留下「一直等待填充」的挂起状态
        if (seq === sceneMetaSeq) {
          this.applyPromptPrefill();
        }
      }
    },

    /**
     * 按 promptKeywords 预填输入框（Task C4）：
     * - 只在有待填充草稿时才动手（open 给了关键字但没给草稿）；
     * - 只在输入框当前为空时填充——用户已经写了内容就绝不覆盖；
     * - 按关键字做「包含匹配」取第一条命中的模板：后端模板是刻意写的中性措辞，
     *   改文案时只要求关键字仍在，不会静默失效；
     * - 没匹配到（或后端未下发模板）就静默降级：不预填、不报错，关键字留着继续给 chips 排序用。
     */
    applyPromptPrefill() {
      if (!this.promptDraftPending) {
        return;
      }
      if (this.draft.trim().length > 0) {
        // 用户已经输入了内容：放弃这次预填
        this.promptDraftPending = false;
        return;
      }
      const matched = this.matchPromptByKeywords(this.promptKeywords);
      if (!matched) {
        return;
      }
      this.draft = matched;
      this.promptDraftPending = false;
    },

    /**
     * 关键字包含匹配：返回当前场景模板里第一条命中任一关键字的模板。
     * 关键字以逗号分隔（中英文逗号都认），全部为空时返回空串。
     */
    matchPromptByKeywords(keywords: string): string {
      const matchers = SplitPromptKeywords(keywords);
      if (matchers.length === 0) {
        return "";
      }
      const prompts = this.sceneList.find((item) => item.key === this.scene)?.prompts ?? [];
      return prompts.find((prompt) => matchers.some((keyword) => prompt.includes(keyword))) ?? "";
    },

    /**
     * 应用一份上下文清单。
     *
     * /ai/scene 的预览与流式首帧的 context 事件共用它：两者同源（服务端同一次 Prepare 的产物），
     * 因此直接覆盖而不做合并——合并只会让「界面上显示的」与「这次真正发出去的」出现差异。
     */
    applyContextItems(items: AiContextItem[], code = 0) {
      this.contextItems = items ?? [];
      this.contextCode = code;
    },

    /** 用 /ai/scene 的结果刷新当前场景的可用性与展示名 */
    applySceneMeta() {      const current = this.sceneList.find((item) => item.key === this.scene);
      this.sceneTitle = current?.title ?? "";
      // 场景列表为空时不改判定（老后端或请求失败时不能把浮窗锁死）
      if (this.sceneList.length > 0) {
        this.sceneEnabled = this.sceneList.some((item) => item.key === this.scene);
      }
    },

    /**
     * 页面漂移检测：当前路由注册了教练入口、且其（场景 + 绑定对象）与 store 当前不一致时，
     * 说明用户已经离开打开教练的页面——继续聊下去上下文会和眼前页面脱节。
     *
     * entry 为 undefined（页面不提供入口）或与当前一致时清掉漂移状态。
     * 由 AiCoachPanel 监听路由变化调用：store 不依赖 router，判定输入从外部注入。
     */
    detectPageDrift(entry: AiCoachPageEntry | undefined) {
      if (!entry) {
        this.pageDrifted = false;
        this.pageDriftEntry = null;
        return;
      }
      const drifted =
        entry.scene !== this.scene || entry.ref.type !== this.ref.type || entry.ref.id !== this.ref.id;
      this.pageDrifted = drifted;
      this.pageDriftEntry = drifted ? entry : null;
    },

    /**
     * 「改用当前页面」：按当前页面的入口重新打开教练。
     * 走页面注册的 openOpts（含禁用原因、预填关键字），与页面自己的按钮行为完全一致；
     * 场景/引用变化时 open 内部会开新会话——旧对话在历史里仍可找回。
     * entryPath 由调用方给（store 不依赖 router）：漂移后的新会话要按「当前页面」的真实路径统计。
     */
    async rebindToCurrentPage(entryPath = "") {
      const entry = this.pageDriftEntry;
      if (!entry) {
        return;
      }
      await this.open({
        scene: entry.scene,
        ref: { type: entry.ref.type, id: entry.ref.id },
        autoFocus: true,
        entryPage: entry.key,
        entryPageTitle: entry.title,
        entryPath,
        ...(entry.openOpts ? entry.openOpts() : {}),
      });
    },

    /** 浮窗内的最近会话列表（切换会话用） */
    async refreshConversations() {
      try {
        const res = await GetAiConversationList({ limit: 20 });
        if (res.code !== 0) {
          return;
        }
        this.conversations = (res.data as { list?: AiConversationItem[] })?.list ?? [];
        this.conversationsLoaded = true;
      } catch (e) {
        console.error("load ai conversation list failed:", e);
      }
    },

    /**
     * 载入一个历史会话（继续对话）：消息用数据库 id 作为气泡 key。
     *
     * opts.openPanel 默认 true，沿用浮窗的既有语义「载入即展开浮窗并聚焦输入框」；
     * 主界面自己渲染对话区，传 false 即可避免同一条会话被浮窗和主界面同时渲染。
     */
    async loadConversation(id: number, opts: { openPanel?: boolean } = {}) {
      if (this.generating) {
        // 生成中切会话会把两段上下文混在一起，直接忽略
        return;
      }
      try {
        const res = await GetAiConversation(id);
        if (res.code !== 0) {
          this.errorCode = res.code;
          this.errorMessage = ResolveAiErrorText("载入会话失败，请稍后重试", res.code);
          return;
        }
        const detail = res.data as AiConversationDetail;
        this.conversationId = detail.conversation.id;
        this.scene = detail.conversation.scene || this.scene;
        this.ref = { type: detail.conversation.ref_type || "", id: detail.conversation.ref_id || 0 };
        // 打开页面随会话走：统计口径（entry_page / entry_path）与展示一致，旧会话缺字段时为空串。
        // 展示名按注册表翻译：库里的 entry_page 是路由 name（如 problem-daily-detail），
        // 直接当标题显示会让上下文条出现英文 key（未登记的 key 由 GetAiCoachPageTitle 原样回退）
        this.entryPage = detail.conversation.entry_page || "";
        this.entryPageTitle = GetAiCoachPageTitle(this.entryPage);
        this.entryPath = detail.conversation.entry_path || "";
        this.messages = MapHistoryMessages(detail.messages ?? []);
        this.lastUsage = null;
        this.errorCode = 0;
        this.errorMessage = "";
        this.activeBubbleKey = "";
        // 历史会话与「当前页面带来的禁用原因」无关，切换会话时清掉；
        // 优先关键字同属「上一个对象」的上下文（如提交状态），一并不再用于新会话的模板排序
        this.sceneNotice = "";
        this.promptKeywords = "";
        this.promptDraftPending = false;
        // 上下文清单属于「上一个对象」：先清空，等 /ai/scene 的预览回来再填。
        // 预览请求失败时宁可空着，也不能让上一个对象的清单继续挂在界面上——那正是预期错位的来源
        this.applyContextItems([]);
        if (opts.openPanel !== false) {
          this.visible = true;
          this.collapsed = false;
          this.focusSignal++;
        }
        this.sceneMetaLoaded = false;
        await this.refreshSceneMeta();
      } catch (e) {
        console.error("load ai conversation failed:", e);
        this.errorMessage = "载入会话失败，请稍后重试";
        this.errorCode = 0;
      }
    },

    /** 开启新会话（保留场景与引用），并打断正在进行的生成 */
    reset() {
      this.abortActive();
      this.conversationId = 0;
      this.messages = [];
      this.draft = "";
      // 预填与排序关键字都属于「上一次打开」的上下文，新会话不再沿用
      this.promptDraftPending = false;
      this.promptKeywords = "";
      this.errorCode = 0;
      this.errorMessage = "";
      this.lastUsage = null;
      this.activeBubbleKey = "";
      this.generating = false;
      // 场景提示属于「上次打开的哪个对象」，新会话不再沿用
      this.sceneNotice = "";
      // 上下文清单同理：新会话绑定的对象可能已经变了，等 /ai/scene 的预览重新算
      this.applyContextItems([]);
    },

    /** 发送一轮消息（text 为空时取草稿） */
    async send(text?: string) {
      const message = (text ?? this.draft).trim();
      if (!message || this.generating) {
        return;
      }
      this.draft = "";
      this.errorCode = 0;
      this.errorMessage = "";
      this.lastUsage = null;
      this.activeBubbleKey = "";
      // 用户气泡先落位：服务端首帧才回话，先显示让用户看到自己发了什么
      this.messages.push(CreateMessage({ key: `u-${++this.bubbleSeq}`, role: "user", content: message }));
      this.generating = true;

      const controller = new AbortController();
      abortController = controller;

      try {
        await PostAiChat(
          {
            conversation_id: this.conversationId,
            scene: this.scene,
            ref: { type: this.ref.type, id: this.ref.id },
            // 打开页面随新建会话落库（续接旧会话时后端以库中记录为准，忽略该字段）
            entry_page: this.entryPage,
            entry_path: this.entryPath,
            message,
          },
          {
            onConversation: (e) => this.applyConversation(e),
            onContext: (items) => this.applyContextItems(items),
            onAssistantStart: (e) => this.applyAssistantStart(e),
            onThinking: (chunk) => this.applyThinking(chunk),
            onChunk: (chunk) => this.applyContent(chunk),
            onToolCall: (e) => this.applyToolCall(e),
            onToolResult: (e) => this.applyToolResult(e),
            onAction: (actions) => this.applyActions(actions),
            onError: (errorMessage, code) => this.applyError(errorMessage, code),
            onInterrupted: (errorMessage) => this.applyInterrupted(errorMessage),
            onDone: (event) => this.applyDone(event),
          },
          controller.signal,
        );
      } catch (e) {
        const errorName = e instanceof Error ? e.name : "";
        if (errorName === "AbortError") {
          // 用户主动停止（或 reset/关闭时打断）：保留已产出内容，按「内容可能不完整」提示
          this.markActiveInterrupted("已停止生成，内容可能不完整");
        } else if (e instanceof AiRequestError) {
          this.applyError(e.message, e.code);
        } else if (e instanceof AiInterruptedError) {
          this.applyInterrupted(e.message);
        } else {
          console.error("ai chat request failed:", e);
          this.applyError("请求失败，请稍后重试", 0);
        }
      } finally {
        if (abortController === controller) {
          abortController = null;
        }
        this.generating = false;
        this.finishActiveBubble();
      }
    },

    /** 停止生成（内容保留，标记为可能不完整） */
    stop() {
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
      this.generating = false;
      this.markActiveInterrupted("已停止生成，内容可能不完整");
      this.finishActiveBubble();
    },

    /** 打断当前请求（不产生任何提示，供 reset / 载入历史时复用） */
    abortActive() {
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
      this.generating = false;
      this.finishActiveBubble();
    },

    // ── 事件应用（与 send 分离，便于单测与复用）──

    /** conversation：首帧回传会话 id 与本轮用户消息 id */
    applyConversation(e: { conversation_id: number; message_id: number }) {
      if (e.conversation_id > 0) {
        this.conversationId = e.conversation_id;
      }
      if (e.message_id > 0) {
        for (let i = this.messages.length - 1; i >= 0; i--) {
          const message = this.messages[i];
          if (message.role === "user" && message.messageId === 0) {
            message.messageId = e.message_id;
            break;
          }
        }
      }
    },

    /** assistant_start：新建一个 assistant 气泡（工具往返的关键，否则第二轮的正文会追加进上一个气泡） */
    applyAssistantStart(e: { round: number }) {
      this.finishActiveBubble();
      const key = `a-r${e.round}-${++this.bubbleSeq}`;
      this.messages.push(CreateMessage({ key, role: "assistant", generating: true }));
      this.activeBubbleKey = key;
    },

    /** thinking 增量 */
    applyThinking(chunk: string) {
      if (!chunk) {
        return;
      }
      this.ensureActiveBubble().thinking += chunk;
    },

    /** content 增量 */
    applyContent(chunk: string) {
      if (!chunk) {
        return;
      }
      this.ensureActiveBubble().content += chunk;
    },

    /** tool_call：记录本次工具调用（渲染为「正在读取…」） */
    applyToolCall(e: { call_id: string; name: string; args: Record<string, unknown> }) {
      const bubble = this.ensureActiveBubble();
      if (bubble.toolCalls.some((call) => call.call_id === e.call_id)) {
        return;
      }
      bubble.toolCalls.push({ call_id: e.call_id, name: e.name, args: e.args });
    },

    /** tool_result：把结果挂到发起该调用的气泡上 */
    applyToolResult(e: { call_id: string; ok: boolean; summary?: string }) {
      const bubble = this.messages.find((message) => message.toolCalls.some((call) => call.call_id === e.call_id)) ?? this.ensureActiveBubble();
      const call = bubble.toolCalls.find((item) => item.call_id === e.call_id);
      const existing = bubble.toolResults.find((item) => item.call_id === e.call_id);
      if (existing) {
        existing.ok = e.ok;
        existing.summary = e.summary;
        return;
      }
      bubble.toolResults.push({ call_id: e.call_id, name: call?.name, ok: e.ok, summary: e.summary });
    },

    /** action：动作卡片（空数组不渲染任何卡片；与 done.actions 去重） */
    applyActions(actions: AiAction[]) {
      if (!actions || actions.length === 0) {
        return;
      }
      const bubble = this.actionTargetBubble();
      actions.forEach((action) => {
        const signature = ActionSignature(action);
        if (bubble.actions.some((item) => ActionSignature(item) === signature)) {
          return;
        }
        bubble.actions.push(action);
      });
    },

    /** done：本轮累计用量与动作 */
    applyDone(event: { usage?: AiStreamUsage; actions?: AiAction[] }) {
      if (event.usage) {
        this.lastUsage = event.usage;
      }
      this.applyActions(event.actions ?? []);
      this.finishActiveBubble();
    },

    /** error 事件：错误码优先查文案表 */
    applyError(message: string, code?: number) {
      this.errorCode = code ?? 0;
      this.errorMessage = ResolveAiErrorText(message, code);
      if (code === 10011) {
        // 凭据不可用：输入区改为引导去设置，避免用户反复发送
        this.byokReady = false;
      }
      if (code === 10095) {
        this.sceneEnabled = false;
      }
      this.finishActiveBubble();
      this.generating = false;
    },

    /** interrupted 事件：内容可能不完整 */
    applyInterrupted(message?: string) {
      this.markActiveInterrupted(message || "生成被中断，内容可能不完整");
      this.finishActiveBubble();
      this.generating = false;
    },

    /**
     * 保存成功：把动作卡片置为「已保存」禁用态。
     *
     * 动作对象来自消息列表（同一个引用），直接改字段即可触发卡片重渲染；
     * 同时清掉 reason——按钮已禁用，不再需要「为什么不能点」的说明。
     */
    markActionSaved(action: AiAction) {
      action.disabled = true;
      action.reason = "";
      action.label = "已保存";
      // 通知提交详情页同步卡片：仅当该动作确实指向某次提交时才发信号
      const judgeId = Number(action.payload?.judge_id ?? 0);
      if (judgeId > 0) {
        this.parseSavedJudgeId = judgeId;
        this.parseSavedTick += 1;
      }
    },

    /**
     * 问答卡片已回答：就地禁用（用户的回答已经作为一条用户消息发出）。
     *
     * 与 markActionSaved 同一手法：动作对象来自消息列表（同一个引用），改字段即触发重渲染。
     * 历史回放不需要它——历史里的问答卡片一律只读（见 MapHistoryMessages）
     */
    markActionAnswered(action: AiAction) {
      action.disabled = true;
      action.reason = "已回答";
    },

    // ── 内部工具 ──

    /** 取当前气泡；没有（如协议里没有 assistant_start）时补建一个，避免增量丢失 */
    ensureActiveBubble(): AiCoachMessage {
      const existing = this.activeBubble;
      if (existing) {
        return existing;
      }
      const key = `a-lazy-${++this.bubbleSeq}`;
      const message = CreateMessage({ key, role: "assistant", generating: true });
      this.messages.push(message);
      this.activeBubbleKey = key;
      return message;
    },

    /** 动作卡片的落点：当前气泡 → 最后一条 assistant 气泡 → 补建一条 */
    actionTargetBubble(): AiCoachMessage {
      const active = this.activeBubble;
      if (active) {
        return active;
      }
      for (let i = this.messages.length - 1; i >= 0; i--) {
        if (this.messages[i].role === "assistant") {
          return this.messages[i];
        }
      }
      return this.ensureActiveBubble();
    },

    /** 结束当前气泡的生成态（其余气泡本身就不在生成中，一并复位更稳） */
    finishActiveBubble() {
      this.messages.forEach((message) => {
        if (message.role === "assistant") {
          message.generating = false;
        }
      });
    },

    /** 给当前气泡打上「内容可能不完整」标记 */
    markActiveInterrupted(message: string) {
      const bubble = this.activeBubble;
      if (!bubble) {
        return;
      }
      bubble.interrupted = true;
      bubble.interruptedMessage = message;
    },
  },

  // 持久化只挑几何与气泡位置（Task C3 / C5）：会话、草稿、生成态都是「本次会话」的临时状态，
  // 落盘既没有意义（消息在服务端），也会把上一轮的临时禁用提示带到下次打开。
  persist: {
    key: "aiCoach",
    pick: ["panelRect", "bubblePos"],
  },
});
